import { FC, MutableRefObject } from "react";
import { User } from "@/types/Users";
import { Signature, SignatureField, SignatureFieldRef } from "@/Features";
import { userApi } from "@/api";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import { bufferToBase64 } from "src/utils/helpers";
import { FormTitle } from "@/components/Forms/components/MonolithFormComponents";
import { Button } from "@monolith-forensics/monolith-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../../../contexts/AuthContext";
import moment from "moment";

interface UserSignatureBlockProps {
  userData: User;
  onSubmit?: () => void;
}

const UserSignatureBlock: FC<UserSignatureBlockProps> = ({
  userData,
  onSubmit,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const sigFieldRef = useRef<SignatureFieldRef>();
  const queryClient = useQueryClient();
  const { setUserInfo } = useAuth();
  const handleSignatureSave = async (signature: Signature | null) => {
    try {
      await userApi.updateUser(userData.user_id, {
        signature: {
          data: signature?.signature,
          created_on: moment().toISOString(),
        },
      });
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      setUserInfo((prevUserInfo: User) => ({
        ...prevUserInfo,
        signature: {
          data: signature?.signature,
          created_on: moment().toISOString(),
        },
      }));
      enqueueSnackbar("Signature updated successfully", { variant: "success" });
      onSubmit?.();
    } catch (error) {
      enqueueSnackbar("Failed to update signature", { variant: "error" });
    }
  };

  const handleClearSignature = async () => {
    try {
      await userApi.updateUser(userData.user_id, {
        signature: null,
      });
      if (sigFieldRef.current?.clear) {
        sigFieldRef.current.clear();
      }
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      setUserInfo((prevUserInfo: User) => ({
        ...prevUserInfo,
        signature: null,
      }));
      enqueueSnackbar("Signature cleared successfully", { variant: "success" });
      onSubmit?.();
    } catch (error) {
      enqueueSnackbar("Failed to clear signature", { variant: "error" });
    }
  };

  return (
    <>
      <div
        style={{
          margin: "35px 0",
        }}
      >
        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <FormTitle data-testid="profile-signature-title">
            User Signature
          </FormTitle>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ width: "50%" }}>
              <SignatureField
                defaultValue={bufferToBase64(userData.signature?.data)}
                onChange={handleSignatureSave}
                sigFieldRef={sigFieldRef}
              />
            </div>
            {userData.signature && (
              <Button
                variant="light"
                color="primary"
                size="xs"
                data-testid="profile-clear-signature-button"
                onClick={handleClearSignature}
                style={{ marginLeft: "10px" }}
              >
                Clear Signature
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSignatureBlock;
